import { Entity } from '@backstage/catalog-model';

export const JIRA_BOARD_MAPPINGS_ANNOTATION = 'invisible.io/jira-board-mappings';
export const IGNORED_JIRA_PROJECT_KEYS = new Set(['YOU_JIRA_KEY']);

export type GroupIndexEntry = {
  entityRef: string;
  name: string;
  parent?: string;
};

function isUsableProjectKey(key: string): boolean {
  return !IGNORED_JIRA_PROJECT_KEYS.has(key.trim().toUpperCase());
}

export function readJiraProjectKeys(entity: Entity): string[] {
  const value = entity.metadata.annotations?.[JIRA_BOARD_MAPPINGS_ANNOTATION];
  if (value) {
    // Accept mixed-case keys and normalize to upper-case, matching how the Jira
    // backend parser reads these annotations; otherwise valid lowercase mappings
    // would appear empty in the org tree and inheritance diagram.
    const keys = [...value.matchAll(/projectKey:\s*["']?([A-Za-z][A-Za-z0-9_]+)/g)]
      .map(match => match[1].toUpperCase())
      .filter(isUsableProjectKey);
    if (keys.length > 0) {
      return [...new Set(keys)].sort();
    }
  }

  const legacy = entity.metadata.annotations?.['atlassian.net/jql'];
  if (legacy) {
    const match = legacy.match(/\bproject\s*=\s*([A-Za-z][A-Z0-9_]+)/i);
    if (match && isUsableProjectKey(match[1])) {
      return [match[1].toUpperCase()];
    }
  }

  const specProject = (entity.spec as { jiraProject?: string } | undefined)?.jiraProject;
  if (specProject && isUsableProjectKey(specProject)) {
    return [specProject.trim().toUpperCase()];
  }

  return [];
}

export function parseOwnerGroupName(owner: unknown): string | undefined {
  if (typeof owner !== 'string') {
    return undefined;
  }

  const trimmed = owner.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.toLowerCase().startsWith('group:')) {
    const namePart = trimmed.split(':')[1];
    if (!namePart) {
      return undefined;
    }
    const name = namePart.split('/').pop();
    return name?.trim() || undefined;
  }

  if (trimmed.includes(':')) {
    return undefined;
  }

  return trimmed;
}

export function buildGroupIndex(entities: Entity[]): Map<string, GroupIndexEntry> {
  const index = new Map<string, GroupIndexEntry>();

  for (const entity of entities) {
    if (entity.kind !== 'Group') {
      continue;
    }

    const name = entity.metadata.name;
    const namespace = entity.metadata.namespace ?? 'default';
    const parent = typeof entity.spec?.parent === 'string' ? entity.spec.parent : undefined;

    index.set(name, {
      entityRef: `group:${namespace}/${name}`,
      name,
      parent,
    });
  }

  return index;
}

export function resolveInheritedProjectKeys(
  startGroupName: string,
  groupIndex: Map<string, GroupIndexEntry>,
  readGroupProjectKeys: (groupName: string) => string[],
): { projectKeys: string[]; inheritedFromGroupRef?: string; inheritedFromName?: string } {
  const visited = new Set<string>();
  let current: string | undefined = startGroupName;

  while (current && !visited.has(current)) {
    visited.add(current);
    const entry = groupIndex.get(current);
    if (!entry) {
      return { projectKeys: [] };
    }

    const projectKeys = readGroupProjectKeys(current);
    if (projectKeys.length > 0) {
      return {
        projectKeys,
        inheritedFromGroupRef: entry.entityRef,
        inheritedFromName: entry.name,
      };
    }

    current = entry.parent;
  }

  return { projectKeys: [] };
}

export function projectKeysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((key, index) => key === right[index]);
}

export function resolveInheritedProjectKeysFromParent(
  startGroupName: string,
  groupIndex: Map<string, GroupIndexEntry>,
  readGroupProjectKeys: (groupName: string) => string[],
): { projectKeys: string[]; inheritedFromGroupRef?: string; inheritedFromName?: string } {
  const entry = groupIndex.get(startGroupName);
  if (!entry?.parent) {
    return { projectKeys: [] };
  }

  return resolveInheritedProjectKeys(entry.parent, groupIndex, readGroupProjectKeys);
}

export function resolveEffectiveProjectKeys(
  entity: Entity,
  groupIndex: Map<string, GroupIndexEntry>,
  groupEntitiesByName: Map<string, Entity>,
): {
  projectKeys: string[];
  source: 'component' | 'group' | 'none';
  inheritedFromGroupRef?: string;
  inheritedFromName?: string;
  ownerGroupRef?: string;
} {
  const explicitProjectKeys = readJiraProjectKeys(entity);
  if (explicitProjectKeys.length > 0) {
    return {
      projectKeys: explicitProjectKeys,
      source: 'component',
    };
  }

  const ownerGroupName = parseOwnerGroupName(entity.spec?.owner);
  if (!ownerGroupName) {
    return {
      projectKeys: [],
      source: 'none',
    };
  }

  const ownerEntry = groupIndex.get(ownerGroupName);
  const ownerGroupRef = ownerEntry?.entityRef ?? `group:default/${ownerGroupName}`;
  const inherited = resolveInheritedProjectKeys(ownerGroupName, groupIndex, groupName => {
    const groupEntity = groupEntitiesByName.get(groupName);
    return groupEntity ? readJiraProjectKeys(groupEntity) : [];
  });

  if (inherited.projectKeys.length === 0) {
    return {
      projectKeys: [],
      source: 'none',
      ownerGroupRef,
    };
  }

  return {
    projectKeys: inherited.projectKeys,
    source: 'group',
    inheritedFromGroupRef: inherited.inheritedFromGroupRef,
    inheritedFromName: inherited.inheritedFromName,
    ownerGroupRef,
  };
}
